import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
    fetchCandidates,
    fetchRecentCandidates,
    fetchCandidatesByStage,
    fetchCandidatesByJob,
    fetchCandidateById,
    createCandidate,
    updateCandidate,
    deleteCandidate,
    changeCandidateStage,
    fetchCandidateDocuments,
    uploadCandidateDocument,
    deleteCandidateDocument,
    fetchCandidateActivities,
    fetchCandidateNotes,
    addCandidateNote,
    updateCandidateNote,
    deleteCandidateNote,
    clearError,
    clearCurrentCandidate,
    clearCandidatesByStage,
    clearCandidatesByJob,
    setPagination,
    clearSearchCache
} from '../slices/candidatesSlice';
import {
    selectCandidates,
    selectCurrentCandidate,
    selectRecentCandidates,
    selectCandidatesByStage,
    selectCandidatesByJob,
    selectCandidatesLoading,
    selectCandidateLoading,
    selectCandidatesError,
    selectCandidateError,
    selectCandidateDocuments,
    selectCandidateActivities,
    selectCandidateNotes,
    selectDocumentsLoading,
    selectActivitiesLoading,
    selectNotesLoading,
    selectPagination,
    selectCandidateStats,
    selectCandidatesSortedByName,
    selectCandidatesSortedByDate,
    selectCandidateById,
    selectCandidatesForStage,
    selectCandidatesForJob,
    selectDocumentsForCandidate,
    selectActivitiesForCandidate,
    selectNotesForCandidate,
    selectDocumentsLoadingForCandidate,
    selectActivitiesLoadingForCandidate,
    selectNotesLoadingForCandidate,
    selectFilteredCandidates,
    selectSortedAndFilteredCandidates,
    selectIsAllCandidatesStale,
    selectIsRecentCandidatesStale,
    selectIsCandidatesByStageStale,
    selectCandidatesByStageStats,
    selectCandidatesBySource,
    selectCandidatesRequiringAction,
    selectCandidatesWithUpcomingInterviews,
    selectCandidateExists
} from '../selectors/candidateSelectors';
import {
    CandidateCreateRequest,
    CandidateUpdateRequest,
    StageChangeRequest
} from '../../types/types';

/**
 * Custom hook for managing candidate state and operations
 * Provides a convenient interface for components to interact with candidate data
 */
export const useCandidates = () => {
    const dispatch = useAppDispatch();

    // Basic selectors
    const candidates = useAppSelector(selectCandidates);
    const currentCandidate = useAppSelector(selectCurrentCandidate);
    const recentCandidates = useAppSelector(selectRecentCandidates);
    const candidatesByStage = useAppSelector(selectCandidatesByStage);
    const candidatesByJob = useAppSelector(selectCandidatesByJob);

    // Loading and error states
    const loading = useAppSelector(selectCandidatesLoading);
    const candidateLoading = useAppSelector(selectCandidateLoading);
    const error = useAppSelector(selectCandidatesError);
    const candidateError = useAppSelector(selectCandidateError);

    // Additional data
    const candidateDocuments = useAppSelector(selectCandidateDocuments);
    const candidateActivities = useAppSelector(selectCandidateActivities);
    const candidateNotes = useAppSelector(selectCandidateNotes);
    const documentsLoading = useAppSelector(selectDocumentsLoading);
    const activitiesLoading = useAppSelector(selectActivitiesLoading);
    const notesLoading = useAppSelector(selectNotesLoading);

    // Pagination and stats
    const pagination = useAppSelector(selectPagination);
    const candidateStats = useAppSelector(selectCandidateStats);
    const candidatesSortedByName = useAppSelector(selectCandidatesSortedByName);
    const candidatesSortedByDate = useAppSelector(selectCandidatesSortedByDate);

    // Staleness checks
    const isAllCandidatesStale = useAppSelector(selectIsAllCandidatesStale);
    const isRecentCandidatesStale = useAppSelector(selectIsRecentCandidatesStale);

    // Analytics data
    const candidatesByStageStats = useAppSelector(selectCandidatesByStageStats);
    const candidatesBySource = useAppSelector(selectCandidatesBySource);
    const candidatesRequiringAction = useAppSelector(selectCandidatesRequiringAction);
    const candidatesWithUpcomingInterviews = useAppSelector(selectCandidatesWithUpcomingInterviews);

    // Basic CRUD operations
    const fetchAll = useCallback(() => {
        return dispatch(fetchCandidates());
    }, [dispatch]);

    const fetchRecent = useCallback(() => {
        return dispatch(fetchRecentCandidates());
    }, [dispatch]);

    const fetchByStage = useCallback((stageId: string) => {
        return dispatch(fetchCandidatesByStage(stageId));
    }, [dispatch]);

    const fetchByJob = useCallback((jobId: string) => {
        return dispatch(fetchCandidatesByJob(jobId));
    }, [dispatch]);

    const fetchById = useCallback((candidateId: string) => {
        return dispatch(fetchCandidateById(candidateId));
    }, [dispatch]);

    const create = useCallback((candidateData: CandidateCreateRequest) => {
        return dispatch(createCandidate(candidateData));
    }, [dispatch]);

    const update = useCallback((id: string, candidateData: CandidateUpdateRequest) => {
        return dispatch(updateCandidate({ id, candidateData }));
    }, [dispatch]);

    const deleteById = useCallback((candidateId: string) => {
        return dispatch(deleteCandidate(candidateId));
    }, [dispatch]);

    const changeStage = useCallback((candidateId: string, stageChangeData: StageChangeRequest) => {
        return dispatch(changeCandidateStage({ candidateId, stageChangeData }));
    }, [dispatch]);

    // Document operations
    const fetchDocuments = useCallback((candidateId: string) => {
        return dispatch(fetchCandidateDocuments(candidateId));
    }, [dispatch]);

    const uploadDocument = useCallback((candidateId: string, file: File) => {
        return dispatch(uploadCandidateDocument({ candidateId, file }));
    }, [dispatch]);

    const deleteDocument = useCallback((candidateId: string, documentId: string) => {
        return dispatch(deleteCandidateDocument({ candidateId, documentId }));
    }, [dispatch]);

    // Activity operations
    const fetchActivities = useCallback((candidateId: string) => {
        return dispatch(fetchCandidateActivities(candidateId));
    }, [dispatch]);

    // Notes operations
    const fetchNotes = useCallback((candidateId: string) => {
        return dispatch(fetchCandidateNotes(candidateId));
    }, [dispatch]);

    const addNote = useCallback((candidateId: string, text: string) => {
        return dispatch(addCandidateNote({ candidateId, text }));
    }, [dispatch]);

    const updateNote = useCallback((noteId: string, text: string) => {
        return dispatch(updateCandidateNote({ noteId, text }));
    }, [dispatch]);

    const deleteNote = useCallback((candidateId: string, noteId: string) => {
        return dispatch(deleteCandidateNote({ candidateId, noteId }));
    }, [dispatch]);

    // State management
    const clearErrors = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    const clearCurrent = useCallback(() => {
        dispatch(clearCurrentCandidate());
    }, [dispatch]);

    const clearStageCache = useCallback((stageId: string) => {
        dispatch(clearCandidatesByStage(stageId));
    }, [dispatch]);

    const clearJobCache = useCallback((jobId: string) => {
        dispatch(clearCandidatesByJob(jobId));
    }, [dispatch]);

    const updatePagination = useCallback((paginationData: Partial<typeof pagination>) => {
        dispatch(setPagination(paginationData));
    }, [dispatch]);

    const clearSearch = useCallback(() => {
        dispatch(clearSearchCache());
    }, [dispatch]);

    // Helper functions
    const getCandidateById = useCallback((candidateId: string) => {
        return candidates.find(candidate => candidate.id === candidateId);
    }, [candidates]);

    const getCandidatesForStage = useCallback((stageId: string) => {
        return candidatesByStage[stageId] || [];
    }, [candidatesByStage]);

    const getCandidatesForJob = useCallback((jobId: string) => {
        return candidatesByJob[jobId] || [];
    }, [candidatesByJob]);

    const getDocumentsForCandidate = useCallback((candidateId: string) => {
        return candidateDocuments[candidateId] || [];
    }, [candidateDocuments]);

    const getActivitiesForCandidate = useCallback((candidateId: string) => {
        return candidateActivities[candidateId] || [];
    }, [candidateActivities]);

    const getNotesForCandidate = useCallback((candidateId: string) => {
        return candidateNotes[candidateId] || [];
    }, [candidateNotes]);

    const isDocumentsLoadingForCandidate = useCallback((candidateId: string) => {
        return documentsLoading[candidateId] || false;
    }, [documentsLoading]);

    const isActivitiesLoadingForCandidate = useCallback((candidateId: string) => {
        return activitiesLoading[candidateId] || false;
    }, [activitiesLoading]);

    const isNotesLoadingForCandidate = useCallback((candidateId: string) => {
        return notesLoading[candidateId] || false;
    }, [notesLoading]);

    const shouldFetchCandidates = useCallback(() => {
        return candidates.length === 0 || isAllCandidatesStale;
    }, [candidates.length, isAllCandidatesStale]);

    const shouldFetchRecentCandidates = useCallback(() => {
        return recentCandidates.length === 0 || isRecentCandidatesStale;
    }, [recentCandidates.length, isRecentCandidatesStale]);

    const shouldFetchCandidatesForStage = useCallback((stageId: string) => {
        return !candidatesByStage[stageId];
    }, [candidatesByStage]);

    // Auto-fetch functions
    const ensureCandidatesLoaded = useCallback(() => {
        if (shouldFetchCandidates()) {
            return fetchAll();
        }
        return Promise.resolve();
    }, [shouldFetchCandidates, fetchAll]);

    const ensureRecentCandidatesLoaded = useCallback(() => {
        if (shouldFetchRecentCandidates()) {
            return fetchRecent();
        }
        return Promise.resolve();
    }, [shouldFetchRecentCandidates, fetchRecent]);

    const ensureCandidatesForStageLoaded = useCallback((stageId: string) => {
        if (shouldFetchCandidatesForStage(stageId)) {
            return fetchByStage(stageId);
        }
        return Promise.resolve();
    }, [shouldFetchCandidatesForStage, fetchByStage]);

    const ensureCandidateLoaded = useCallback((candidateId: string) => {
        const exists = candidates.some(c => c.id === candidateId);
        if (!exists && !candidateLoading) {
            return fetchById(candidateId);
        }
        return Promise.resolve();
    }, [candidates, candidateLoading, fetchById]);

    return {
        // State
        candidates,
        currentCandidate,
        recentCandidates,
        candidatesByStage,
        candidatesByJob,
        candidateDocuments,
        candidateActivities,
        candidateNotes,
        candidatesSortedByName,
        candidatesSortedByDate,

        // Loading states
        loading,
        candidateLoading,
        documentsLoading,
        activitiesLoading,
        notesLoading,

        // Error states
        error,
        candidateError,

        // Pagination and stats
        pagination,
        candidateStats,
        candidatesByStageStats,
        candidatesBySource,
        candidatesRequiringAction,
        candidatesWithUpcomingInterviews,

        // Staleness checks
        isAllCandidatesStale,
        isRecentCandidatesStale,

        // Basic operations
        fetchAll,
        fetchRecent,
        fetchByStage,
        fetchByJob,
        fetchById,
        create,
        update,
        deleteById,
        changeStage,

        // Document operations
        fetchDocuments,
        uploadDocument,
        deleteDocument,

        // Activity operations
        fetchActivities,

        // Notes operations
        fetchNotes,
        addNote,
        updateNote,
        deleteNote,

        // State management
        clearErrors,
        clearCurrent,
        clearStageCache,
        clearJobCache,
        updatePagination,
        clearSearch,

        // Helper functions
        getCandidateById,
        getCandidatesForStage,
        getCandidatesForJob,
        getDocumentsForCandidate,
        getActivitiesForCandidate,
        getNotesForCandidate,
        isDocumentsLoadingForCandidate,
        isActivitiesLoadingForCandidate,
        isNotesLoadingForCandidate,

        // Auto-fetch functions
        ensureCandidatesLoaded,
        ensureRecentCandidatesLoaded,
        ensureCandidatesForStageLoaded,
        ensureCandidateLoaded,

        // Validation helpers
        shouldFetchCandidates,
        shouldFetchRecentCandidates,
        shouldFetchCandidatesForStage
    };
};

/**
 * Hook specifically for getting a single candidate by ID
 * Automatically fetches the candidate if not found in state
 */
export const useCandidateById = (candidateId: string | undefined) => {
    const candidate = useAppSelector(state => candidateId ? selectCandidateById(state, candidateId) : null);
    const loading = useAppSelector(selectCandidateLoading);
    const error = useAppSelector(selectCandidateError);
    const candidateExists = useAppSelector(state => candidateId ? selectCandidateExists(state, candidateId) : false);
    const { ensureCandidateLoaded } = useCandidates();

    // Auto-fetch if candidate not found and not loading
    React.useEffect(() => {
        if (candidateId && !candidateExists && !loading) {
            ensureCandidateLoaded(candidateId);
        }
    }, [candidateId, candidateExists, loading, ensureCandidateLoaded]);

    return {
        candidate,
        loading,
        error,
        found: candidateExists
    };
};

/**
 * Hook for managing candidate documents
 */
export const useCandidateDocuments = (candidateId: string | undefined) => {
    const documents = useAppSelector(state => candidateId ? selectDocumentsForCandidate(state, candidateId) : []);
    const loading = useAppSelector(state => candidateId ? selectDocumentsLoadingForCandidate(state, candidateId) : false);
    const { fetchDocuments, uploadDocument, deleteDocument } = useCandidates();

    // Auto-fetch documents when candidateId changes
    React.useEffect(() => {
        if (candidateId && documents.length === 0 && !loading) {
            fetchDocuments(candidateId);
        }
    }, [candidateId, documents.length, loading, fetchDocuments]);

    return {
        documents,
        loading,
        fetchDocuments: candidateId ? () => fetchDocuments(candidateId) : () => Promise.resolve(),
        uploadDocument: candidateId ? (file: File) => uploadDocument(candidateId, file) : () => Promise.resolve(),
        deleteDocument: candidateId ? (documentId: string) => deleteDocument(candidateId, documentId) : () => Promise.resolve()
    };
};

/**
 * Hook for managing candidate notes
 */
export const useCandidateNotes = (candidateId: string | undefined) => {
    const notes = useAppSelector(state => candidateId ? selectNotesForCandidate(state, candidateId) : []);
    const loading = useAppSelector(state => candidateId ? selectNotesLoadingForCandidate(state, candidateId) : false);
    const { fetchNotes, addNote, updateNote, deleteNote } = useCandidates();

    // Auto-fetch notes when candidateId changes
    React.useEffect(() => {
        if (candidateId && notes.length === 0 && !loading) {
            fetchNotes(candidateId);
        }
    }, [candidateId, notes.length, loading, fetchNotes]);

    return {
        notes,
        loading,
        fetchNotes: candidateId ? () => fetchNotes(candidateId) : () => Promise.resolve(),
        addNote: candidateId ? (text: string) => addNote(candidateId, text) : () => Promise.resolve(),
        updateNote,
        deleteNote: candidateId ? (noteId: string) => deleteNote(candidateId, noteId) : () => Promise.resolve()
    };
};

/**
 * Hook for managing candidate activities
 */
export const useCandidateActivities = (candidateId: string | undefined) => {
    const activities = useAppSelector(state => candidateId ? selectActivitiesForCandidate(state, candidateId) : []);
    const loading = useAppSelector(state => candidateId ? selectActivitiesLoadingForCandidate(state, candidateId) : false);
    const { fetchActivities } = useCandidates();

    // Auto-fetch activities when candidateId changes
    React.useEffect(() => {
        if (candidateId && activities.length === 0 && !loading) {
            fetchActivities(candidateId);
        }
    }, [candidateId, activities.length, loading, fetchActivities]);

    return {
        activities,
        loading,
        fetchActivities: candidateId ? () => fetchActivities(candidateId) : () => Promise.resolve()
    };
};

export default useCandidates;